/*
 * Flash Deals — colour-scoped active sales.
 *
 * DATA LAYER: window.HealixColors only — no raw fetch() in this file, no
 * hardcoded API URLs.
 *   - HealixColors.deals()         → which products are on sale + the deal meta
 *                                    (discountLabel, discountCode, colour, hex,
 *                                    edition). This is the SOLE source of deals.
 *   - HealixColors.product(handle) → enriches a deal handle into the full
 *                                    storefront product (images, media, options,
 *                                    variants, pricing). REQUIRED because the
 *                                    deals payload returns NO image/price (audited
 *                                    live: products contain only handle, title,
 *                                    color, colorName, hex, edition, discountLabel,
 *                                    saleId). This reuses the theme's existing
 *                                    /products/{handle}.js enrichment pattern
 *                                    (see healix-colors.enrichLinkedItem and the
 *                                    Department Section PDP) rather than inventing
 *                                    data or duplicating fetch logic.
 *
 * REUSE: cards + mini-PDP are built against the Department Section class contract
 * (.dept__product-card*, .dept__pdp*, .dept__skeleton*) styled by
 * department-section.css; the mini-PDP mirrors the Department Section PDP
 * (gallery, thumbnails, media switching, size selection, add-to-cart, pricing,
 * animation, smooth scroll, active card state). Rows reuse the global
 * `scroll-arrows` snippet. Money + cart use the theme globals (window.formatMoney
 * / window.themeMoneyFormat / window.CartAPI). Nothing in the live Department
 * Section is modified.
 */
(function () {
  'use strict';

  // ── Utilities (mirrors of Department Section helpers) ──────────────────────
  function esc(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // Resize a Shopify CDN image URL (same transform the Department Section uses).
  function resizeImage(src, size) {
    if (!src) return '';
    if (typeof src !== 'string') src = src.src || '';
    if (!src) return '';
    var parts = src.split('?');
    var base = parts[0];
    var query = parts[1] ? '?' + parts[1] : '';
    var resized = base.replace(/(\.(jpg|jpeg|png|gif|webp))$/i, '_' + size + 'x$1');
    if (resized === base) return src;
    return resized + query;
  }

  // Canonical Healix money handling (mirrors department-section / set-builder):
  // integer >= 1000 is treated as cents, otherwise as a major-unit amount.
  function normalizeMoneyInput(rawValue) {
    if (rawValue === null || rawValue === undefined || rawValue === '') return { major: 0, cents: 0 };
    if (typeof rawValue === 'string') {
      var s = rawValue.replace(/,/g, '').trim();
      var m = Number(s);
      if (!isFinite(m)) return { major: 0, cents: 0 };
      return { major: m, cents: Math.round(m * 100) };
    }
    var n = Number(rawValue);
    if (!isFinite(n)) return { major: 0, cents: 0 };
    if (Number.isInteger(n) && Math.abs(n) >= 1000) return { major: n / 100, cents: Math.round(n) };
    return { major: n, cents: Math.round(n * 100) };
  }

  function money(rawValue) {
    var m = normalizeMoneyInput(rawValue);
    if (window.formatMoney) return window.formatMoney(m.cents, window.themeMoneyFormat);
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && window.Shopify.money_format) {
      return window.Shopify.formatMoney(m.cents, window.Shopify.money_format);
    }
    if (window.formatMoneyFallback) return window.formatMoneyFallback(m.cents);
    return '$' + m.major.toFixed(2);
  }

  // Format a value that is ALREADY in cents — bypasses normalizeMoneyInput's
  // dollars/cents heuristic (which wrongly treats sub-1000-cent amounts like a
  // $4.37 saving = 437¢ as $437). Use for computed cents (deal/regular/savings).
  function moneyFromCents(cents) {
    var c = Math.round(Number(cents) || 0);
    if (window.formatMoney) return window.formatMoney(c, window.themeMoneyFormat);
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && window.Shopify.money_format) {
      return window.Shopify.formatMoney(c, window.Shopify.money_format);
    }
    if (window.formatMoneyFallback) return window.formatMoneyFallback(c);
    return '$' + (c / 100).toFixed(2);
  }

  // Original/MSRP price (cents) for a price-edit deal. Price-edit sales change
  // the Shopify variant price and put the pre-sale price in compare-at.
  function regularPriceCents(deal, product, variant, dealCents) {
    var compareRaw = (variant && variant.compare_at_price) ||
      (product && (product.compare_at_price || product.compare_at_price_max));
    var compareCents = compareRaw ? normalizeMoneyInput(compareRaw).cents : 0;
    if (compareCents > dealCents) return compareCents;
    return 0;
  }

  // Automatic/code discounts do not change product.js prices: Shopify returns
  // the regular price and applies the discount later. Calculate the displayed
  // Flash Deal price downward from that regular price using the sale contract.
  function applySaleDiscountCents(regularCents, sale) {
    var value = sale && sale.value;
    var amount = Number(value && value.amount);
    if (!value || !isFinite(amount) || amount <= 0) return regularCents;
    if (value.type === 'percentage') {
      if (amount >= 100) return 0;
      return Math.max(0, Math.round(regularCents * (1 - amount / 100)));
    }
    if (value.type === 'fixed') {
      return Math.max(0, regularCents - Math.round(amount * 100));
    }
    return regularCents;
  }

  function dealPriceBreakdownCents(deal, product, variant) {
    var currentCents = normalizeMoneyInput(variant ? variant.price : product.price).cents;
    var sale = deal && deal._sale;
    var mechanism = String((sale && sale.mechanism) || '').toLowerCase();
    if (mechanism === 'automatic') {
      var shared = window.HealixColors && typeof window.HealixColors.flashPriceBreakdown === 'function'
        ? window.HealixColors.flashPriceBreakdown(variant || product, sale)
        : null;
      var compareRaw = (variant && variant.compare_at_price) ||
        (product && (product.compare_at_price || product.compare_at_price_max));
      var compareCents = compareRaw ? normalizeMoneyInput(compareRaw).cents : 0;
      return {
        regular: shared ? shared.sellingCents : currentCents,
        deal: shared ? shared.flashCents : applySaleDiscountCents(currentCents, sale),
        list: shared ? shared.listCents : (compareCents > currentCents ? compareCents : 0),
        automatic: true
      };
    }
    if (mechanism === 'code') {
      return {
        regular: currentCents,
        deal: applySaleDiscountCents(currentCents, sale),
        list: 0,
        automatic: false
      };
    }
    return {
      regular: regularPriceCents(deal, product, variant, currentCents),
      deal: currentCents,
      list: 0,
      automatic: false
    };
  }

  function productUrl(handle) {
    var routeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return routeRoot.replace(/\/+$/, '') + '/products/' + encodeURIComponent(handle);
  }

  function isLimited(editionLike) {
    return String(editionLike || '').toLowerCase() === 'limited';
  }

  function getDiscountCode(deal, saleMap) {
    var sale = (deal && saleMap[deal.saleId]) || {};
    return String(
      (deal && (deal.discountCode || deal.discount_code || deal.code)) || sale.code || sale.discountCode || ''
    ).trim();
  }

  function buildSaleMap(sales) {
    var map = {};
    (Array.isArray(sales) ? sales : []).forEach(function (s) {
      if (s && s.saleId) map[s.saleId] = s;
    });
    return map;
  }

  // ── Variant helpers (mirror Department Section) ────────────────────────────
  function isVariantAvailable(variant) {
    if (!variant) return false;
    if (typeof variant.available === 'undefined') return true;
    return !!variant.available;
  }
  function findFirstAvailableVariant(variants) {
    if (!Array.isArray(variants) || !variants.length) return null;
    return variants.find(function (v) { return isVariantAvailable(v); }) || null;
  }
  function findMatchingVariant(variants, selectedByIndex, requireAvailable) {
    if (!Array.isArray(variants) || !variants.length) return null;
    return variants.find(function (v) {
      if (!v) return false;
      if (requireAvailable && !isVariantAvailable(v)) return false;
      var o1 = selectedByIndex[0] === undefined || v.option1 === selectedByIndex[0];
      var o2 = selectedByIndex[1] === undefined || v.option2 === selectedByIndex[1];
      var o3 = selectedByIndex[2] === undefined || v.option3 === selectedByIndex[2];
      return o1 && o2 && o3;
    }) || null;
  }

  // ── Skeleton / empty ───────────────────────────────────────────────────────
  function renderSkeleton(bodyEl) {
    var cards = '';
    for (var c = 0; c < 8; c++) {
      cards +=
        '<div class="deals-product deals-product--skeleton" aria-hidden="true">' +
          '<div class="flash-deals__skel-img"></div>' +
          '<div class="flash-deals__skel-line"></div>' +
          '<div class="flash-deals__skel-line flash-deals__skel-line--short"></div>' +
          '<div class="flash-deals__skel-btn"></div>' +
        '</div>';
    }
    bodyEl.innerHTML = '<div class="flash-deals__grid">' + cards + '</div>';
    bodyEl.setAttribute('aria-busy', 'true');
  }

  function renderEmpty(bodyEl, text, showEmpty) {
    bodyEl.setAttribute('aria-busy', 'false');
    if (!showEmpty) { bodyEl.innerHTML = ''; return; }
    bodyEl.innerHTML =
      '<div class="flash-deals__empty" role="status">' +
        '<svg class="flash-deals__empty-icon" viewBox="0 0 24 24" width="40" height="40" fill="none" ' +
          'stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
          '<path d="M13 2 3 14h7l-1 8 11-12h-7l1-8z"/>' +
        '</svg>' +
        '<p class="flash-deals__empty-text">' + esc(text) + '</p>' +
      '</div>';
  }

  // Department Section PDP skeleton markup (reused verbatim by class contract).
  function loadingPDPHTML() {
    return (
      '<div class="dept__pdp-mount dept__pdp-mount--loading">' +
        '<div class="dept__loading-skeleton pdp dept__pdp-skeleton-layer">' +
          '<div class="dept__skeleton-gallery">' +
            '<div class="dept__skeleton-thumbs">' +
              '<div class="dept__skeleton-thumb"></div><div class="dept__skeleton-thumb"></div>' +
              '<div class="dept__skeleton-thumb"></div><div class="dept__skeleton-thumb"></div>' +
            '</div>' +
            '<div class="dept__skeleton-main-img"></div>' +
          '</div>' +
          '<div class="dept__skeleton-info">' +
            '<div class="dept__skeleton-title"></div>' +
            '<div class="dept__skeleton-price"></div>' +
            '<div class="dept__skeleton-options"></div>' +
            '<div class="dept__skeleton-options dept__skeleton-options--short"></div>' +
            '<div class="dept__skeleton-btn"></div>' +
            '<div class="dept__skeleton-badges"></div>' +
          '</div>' +
        '</div>' +
      '</div>'
    );
  }

  // ── Category helpers (reuse the collection page's custom.category metafield) ─
  // handleize mirrors Liquid's `| handleize` closely enough for filter matching.
  function handleizeJS(value) {
    return String(value == null ? '' : value)
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  // Stable per-handle "claimed" percentage (no backend supplies one). Deterministic
  // so it does not flicker across reloads; biased high to read as urgency.
  function claimedPercent(handle) {
    var s = String(handle || ''), h = 0;
    for (var i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
    return 60 + (h % 33); // 60–92%
  }

  // Shorten a full product title into a "family" label by dropping the leading
  // gender word and the colour name (keeps the data-attr; no category derivation).
  function familyLabel(deal) {
    var title = String(deal.title || '').trim();
    var out = title;
    var colorName = String(deal.colorName || deal.color || '').replace(/_/g, ' ').trim();
    if (colorName) out = out.replace(new RegExp(colorName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i'), ' ');
    out = out.replace(/^\s*(women['’]?s|womens|women|men['’]?s|mens|men|kids|unisex)\s+/i, '');
    out = out.replace(/\s{2,}/g, ' ').trim();
    return out || title;
  }

  // Handle-based gender detection — the SAME conventions the collection template
  // uses (assets/collection.js + blocks/collection-gender-tabs.liquid):
  //   men   = handle contains '-men' OR starts with 'men-'/'mens-'
  //   women = handle contains '-women' OR starts with 'women-'/'womens-'
  // Men is tested first (mirrors collection.js); "-women" never matches "-men".
  function genderFromHandle(handle) {
    var h = String(handle == null ? '' : handle).toLowerCase();
    if (h.indexOf('-men') !== -1 || h.indexOf('men-') === 0 || h.indexOf('mens-') === 0) return 'men';
    if (h.indexOf('-women') !== -1 || h.indexOf('women-') === 0 || h.indexOf('womens-') === 0) return 'women';
    return '';
  }

  // Fallback for products whose handle carries no gender token: the Shopify
  // product-type prefix ("Women …" / "Men …"), consistent with department-section.js.
  function genderFromType(productType) {
    var t = String(productType == null ? '' : productType).toLowerCase().trim();
    if (/^women'?s?\b/.test(t)) return 'women'; // women first ("women" contains "men")
    if (/^men'?s?\b/.test(t)) return 'men';
    return '';
  }

  // Per-product gender, handle-convention first (collection-template parity),
  // product type as fallback. Empty string = shared / gender-neutral (e.g. caps).
  function genderForDeal(deal) {
    return genderFromHandle(deal && deal.handle) || genderFromType(deal && deal._category);
  }

  // Title-case a label so pills read consistently ("CAPS" -> "Caps",
  // "HEADBAND" -> "Headband") while leaving separators intact ("Boxers & More").
  function titleCase(str) {
    return String(str == null ? '' : str)
      .toLowerCase()
      .replace(/\b([a-z])/g, function (_, c) { return c.toUpperCase(); });
  }

  // Display label for a category pill — strips the leading gender word so both
  // genders read cleanly ("Women Tops" -> "Tops", "Men Pants" -> "Pants"), then
  // normalises capitalisation. Filtering is unaffected (the pill key uses the raw
  // product type via handleizeJS, not this label).
  function categoryLabel(productType) {
    var s = String(productType == null ? '' : productType).trim();
    var stripped = s.replace(/^\s*(women'?s?|men'?s?)\s+/i, '').trim() || s;
    return titleCase(stripped);
  }

  // ── Card (matches the reference deals-product contract) ────────────────────
  function buildCard(deal) {
    var handle = String(deal.handle || '').trim();
    var family = esc(deal._family || deal.title || handle);
    var discountLabel = String(deal.discountLabel || '').trim().replace(/^flash deals?$/i, 'Clearance');
    var catKey = handleizeJS(deal._category || '');
    var pct = claimedPercent(handle);

    var card = document.createElement('div');
    card.className = 'deals-product js-deals-product flash-deals__card';
    card.setAttribute('data-flash-deals-card', '');
    card.setAttribute('data-handle', handle);
    card.setAttribute('data-product-handle', handle);
    card.setAttribute('data-deals-category', catKey);
    card.setAttribute('data-deals-gender', deal._gender || '');
    card.setAttribute('data-deals-product-family-label', deal._family || deal.title || '');
    card.setAttribute('data-deals-product-title', deal.title || '');
    card.setAttribute('data-deals-product-department', deal.colorName || deal.color || '');
    card.setAttribute('data-is-app-exclusive', 'false');
    card.setAttribute('data-regular-price', '0');
    card.setAttribute('data-deal-price', '0');
    card.setAttribute('data-total-savings', '0');
    card.setAttribute('target-element-id', handle);
    card.setAttribute('aria-label', 'View deal: ' + (deal.title || handle));

    card.innerHTML =
      '<div class="deals-product-top">' +
        '<button type="button" class="deals-product-top__left--image-container js-claim-deal-button" aria-label="View deal: ' + esc(deal.title || handle) + '">' +
          (discountLabel ? '<span class="flash-deals__badge">' + esc(discountLabel) + '</span>' : '') +
          '<div class="flash-deals__img-fallback" data-fd-img aria-hidden="true"></div>' +
        '</button>' +
        '<div class="deals-product-top__right">' +
          '<h4 class="deals-product-top__right--title">' + family + '</h4>' +
          '<div class="deals-product-top__right-price-compare">' +
            '<div class="deals-product-top__right-msrp deals-product-top__right-stat-card" data-fd-regular-box hidden>' +
              '<p class="deals-product-top__right-msrp--label deals-product-top__right-stat-card--label">Regular Price</p>' +
              '<p class="deals-product-top__right-msrp--price" data-fd-regular></p>' +
            '</div>' +
            '<div class="deals-product-top__right-deal">' +
              '<p class="deals-product-top__right-deal--label">Clearance</p>' +
              '<p class="deals-product-top__right-deal--price" data-fd-deal></p>' +
            '</div>' +
          '</div>' +
        '</div>' +
      '</div>' +
      '<div class="deals-product-bottom">' +
        '<button type="button" class="deals-product-bottom__claim-deal js-claim-deal-button">' +
          '<div class="deals-product-bottom__claim-deal--text">Claim Deal Now</div>' +
          '<div class="deals-product-bottom__claim-deal--pulse" aria-hidden="true"></div>' +
        '</button>' +
        '<div class="deals-product-top__right-total-savings" data-fd-savings-box hidden>' +
          '<p class="deals-product-top__right-total-savings--label">' +
            'Total Savings ' +
            '<span class="deals-product-top__right-total-savings--price" data-fd-savings></span>' +
          '</p>' +
        '</div>' +
        '<div class="deals-product-bottom__claimed js-claimed-product" data-percentage-claimed="' + pct + '">' +
          '<div class="deals-product-bottom__claimed--progress-bar">' +
            '<div class="deals-product-bottom__claimed--progress-bar-completed js-progress-bar-completed" style="width: ' + pct + '%;"></div>' +
          '</div>' +
          '<div class="deals-product-bottom__claimed--text js-claimed-text" data-percentage-claimed="' + pct + '">' + pct + '% claimed</div>' +
        '</div>' +
      '</div>';
    return card;
  }

  // Fill a card's image, price boxes, savings + price data-attrs from the
  // enriched product (async). Savings = regular (compare-at) − flash (price).
  function enrichCard(card, deal) {
    if (!window.HealixColors || typeof window.HealixColors.product !== 'function') return;
    window.HealixColors.product(deal.handle).then(function (p) {
      if (!p) return;

      var imgSlot = card.querySelector('[data-fd-img]');
      var imgSrc = p.featured_image || (p.images && p.images[0]) || '';
      if (imgSlot && imgSrc) {
        var img = document.createElement('img');
        img.src = resizeImage(imgSrc, 500);
        img.alt = deal.title || p.title || '';
        img.loading = 'lazy';
        img.decoding = 'async';
        imgSlot.replaceWith(img);
      }

      var fv = findFirstAvailableVariant(p.variants) || (p.variants && p.variants[0]);
      var prices = dealPriceBreakdownCents(deal, p, fv);
      var dealCents = prices.deal;
      var regularCents = prices.regular;
      var savingsCents = regularCents > dealCents ? regularCents - dealCents : 0;

      var dealEl = card.querySelector('[data-fd-deal]');
      if (dealEl) dealEl.textContent = moneyFromCents(dealCents);

      var regularBox = card.querySelector('[data-fd-regular-box]');
      var regularEl = card.querySelector('[data-fd-regular]');
      if (regularEl && regularBox && savingsCents > 0) {
        regularEl.textContent = moneyFromCents(regularCents);
        regularBox.hidden = false;
      }

      var savingsBox = card.querySelector('[data-fd-savings-box]');
      var savingsEl = card.querySelector('[data-fd-savings]');
      if (savingsEl && savingsBox && savingsCents > 0) {
        savingsEl.textContent = moneyFromCents(savingsCents);
        savingsBox.hidden = false;
      }

      card.setAttribute('data-regular-price', String(regularCents));
      card.setAttribute('data-deal-price', String(dealCents));
      card.setAttribute('data-total-savings', String(savingsCents));
    });
  }

  // ── Category pills ─────────────────────────────────────────────────────────
  // `types` are full product.type strings; the pill key matches the card's
  // data-deals-category (handleized full type), the label is the stripped form.
  function buildPills(filtersEl, types) {
    var html =
      '<button type="button" class="flash-deals__pill is-active" data-fd-pill="" aria-pressed="true">All</button>';
    types.forEach(function (t) {
      html +=
        '<button type="button" class="flash-deals__pill" data-fd-pill="' + esc(handleizeJS(t)) + '" aria-pressed="false">' +
          esc(categoryLabel(t)) + '</button>';
    });
    filtersEl.innerHTML = html;
  }

  // ── Header countdown (auto-resets daily at 12:00 AM Pacific) ──────────────
  // No manual date: the timer always counts down to the next 12:00 AM PT and
  // restarts at 24:00:00, every day. DST-safe via America/Los_Angeles.
  function makeDayKey(tz) {
    var dtf = new Intl.DateTimeFormat('en-US', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' });
    return function dayKey(ms) { return dtf.format(ms); };
  }

  // Epoch ms of the next 12:00 AM in `tz` strictly after `now`.
  function nextMidnight(now, tz) {
    tz = tz || 'America/Los_Angeles';
    var dayKey = makeDayKey(tz);
    now = typeof now === 'number' ? now : Date.parse(now);
    var target = dayKey(now);
    var lo, hi;
    for (var i = 1; i <= 26; i++) {
      hi = now + i * 3600 * 1000;
      if (dayKey(hi) !== target) { lo = hi - 3600 * 1000; break; }
      if (i === 26) return null;
    }
    var a = lo, b = hi;
    while (b - a > 1) {
      var mid = a + Math.floor((b - a) / 2);
      if (dayKey(mid) !== target) b = mid; else a = mid;
    }
    return b;
  }

  function initCountdown(root) {
    var el = root.querySelector('[data-flash-deals-countdown]');
    if (!el) return;
    var hh = el.querySelector('[data-fd-hh]');
    var mm = el.querySelector('[data-fd-mm]');
    var ss = el.querySelector('[data-fd-ss]');
    function pad(n) { return n < 10 ? '0' + n : '' + n; }
    function tick() {
      var deadline = nextMidnight(Date.now());
      if (deadline == null) return;
      var diff = Math.floor((deadline - Date.now()) / 1000);
      if (diff <= 0) return;
      var h = Math.floor(diff / 3600);
      var m = Math.floor((diff % 3600) / 60);
      var s = diff % 60;
      if (hh) hh.textContent = pad(h);
      if (mm) mm.textContent = pad(m);
      if (ss) ss.textContent = pad(s);
    }
    tick();
    if (root._fdCountdownTimer) clearInterval(root._fdCountdownTimer);
    root._fdCountdownTimer = setInterval(tick, 1000);
  }

  // ── Mini-PDP build (mirrors Department Section renderPDP) ───────────────────
  function buildMediaList(product) {
    var mediaList = [];
    var media = Array.isArray(product.media) ? product.media : [];
    media.forEach(function (m) {
      if (m && m.media_type === 'video' && Array.isArray(m.sources) && m.sources.length) {
        mediaList.push({
          id: m.id,
          type: 'video',
          poster: (m.preview_image && m.preview_image.src) || '',
          sources: m.sources
        });
      } else if (m && m.media_type === 'image') {
        mediaList.push({ id: m.id, type: 'image', src: m.src || (m.preview_image && m.preview_image.src) || '' });
      }
    });
    if (!mediaList.length) {
      (Array.isArray(product.images) ? product.images : []).forEach(function (src) {
        mediaList.push({ type: 'image', src: src });
      });
    }
    return mediaList;
  }

  function miniProductForm() {
    return window.HealixProductForm || null;
  }

  function miniSelectionByName(options, selectedByIndex) {
    var selected = {};
    options.forEach(function (option, index) {
      selected[option.name] = Object.prototype.hasOwnProperty.call(selectedByIndex, index)
        ? selectedByIndex[index]
        : null;
    });
    return selected;
  }

  function miniMissingOptions(options, selectedByIndex) {
    return options.filter(function (option, index) {
      return !Object.prototype.hasOwnProperty.call(selectedByIndex, index) || selectedByIndex[index] == null;
    });
  }

  function miniPendingLabel(options, selectedByIndex) {
    var missing = miniMissingOptions(options, selectedByIndex);
    if (!missing.length) return '';
    return missing.length === 1
      ? 'Select a ' + String(missing[0].name).toLowerCase()
      : 'Select options';
  }

  function isDeferredPdpOption(name) {
    var normalized = String(name || '').trim().toLowerCase();
    return normalized === 'size' || normalized === 'length';
  }

  function lengthHelper(value, product) {
    var men = genderFromType(product && product.type) === 'men';
    switch (String(value || '').trim().toLowerCase()) {
      case 'petite': return men ? '29&quot; - Up to 5\'7&quot;' : '29&quot; - Up to 5\'3&quot;';
      case 'regular': return men ? '31&quot; - 5\'8&quot; to 5\'11&quot;' : '31&quot; - 5\'4&quot; to 5\'7&quot;';
      case 'tall': return men ? '33&quot; - 6\'0&quot; and above' : '33&quot; - 5\'8&quot; and above';
      default: return '';
    }
  }

  function miniFlashZapHTML() {
    return '<span class="product-price-block__flash-zap flash-deals__pdp-zap" data-pdp-flash-zap aria-hidden="true">' +
      '<svg data-flash-zap-icon viewBox="0 0 256 256" aria-hidden="true" focusable="false">' +
      '<path d="M215.79,118.17a8,8,0,0,0-5-5.66L153.18,90.9l14.66-73.33a8,8,0,0,0-13.69-7l-112,120a8,8,0,0,0,3,13l57.63,21.61L88.16,238.43a8,8,0,0,0,13.69,7l112-120A8,8,0,0,0,215.79,118.17Z"></path>' +
      '</svg></span>';
  }

  function flashColorHex(value) {
    var hex = String(value || '').trim();
    if (!/^#?[0-9a-f]{3}([0-9a-f]{3})?$/i.test(hex)) return '';
    return hex.charAt(0) === '#' ? hex : '#' + hex;
  }

  function flashColorText(hex) {
    var value = String(hex || '').replace('#', '');
    if (value.length === 3) value = value[0] + value[0] + value[1] + value[1] + value[2] + value[2];
    if (value.length !== 6) return '#FFFFFF';
    var r = parseInt(value.slice(0, 2), 16) / 255;
    var g = parseInt(value.slice(2, 4), 16) / 255;
    var b = parseInt(value.slice(4, 6), 16) / 255;
    return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.6 ? '#1D2129' : '#FFFFFF';
  }

  function flashColorTagHTML(name, hex, slug) {
    if (!name) return '';
    var safeHex = flashColorHex(hex);
    return '<span class="tag tag--mini tag--solid product-card__badge ' + (safeHex ? 'tag--hex' : 'tag--default') + '"' +
      (safeHex ? ' style="--tag-hex:' + safeHex + ';--tag-hex-text:' + flashColorText(safeHex) + ';"' : '') +
      (slug ? ' data-color-slug="' + esc(slug) + '"' : '') + '><span class="tag__text">' + esc(name) + '</span></span>';
  }

  function flashColorOverlayHTML(name, hex, slug) {
    var tag = flashColorTagHTML(name, hex, slug);
    return tag ? '<div data-product-image-color-badge class="flash-deals__native-color-overlay">' + tag + '</div>' : '';
  }

  function nativeMediaMarkup(media, product, index, context, colorOverlayHTML) {
    var mediaId = esc(media.id || ('flash-media-' + index));
    var wrapperClass = 'product-media-gallery__image-wrapper';
    var mediaClass = context === 'mobile'
      ? 'product-media-gallery__carousel-media product-media-gallery__image'
      : 'product-media-gallery__image product-media-gallery__featured-image';
    if (media.type === 'video') {
      var sources = (media.sources || []).map(function (source) {
        return '<source src="' + esc(source.url) + '" type="' + esc(source.mime_type || 'video/mp4') + '">';
      }).join('');
      return '<div class="product-media-gallery__video-wrapper">' +
        '<video class="product-media-gallery__video" muted autoplay playsinline preload="metadata"' +
          (media.poster ? ' poster="' + esc(media.poster) + '"' : '') + '>' + sources + '</video>' + colorOverlayHTML + '</div>';
    }
    return '<div class="' + wrapperClass + '">' +
      '<img src="' + esc(resizeImage(media.src, context === 'mobile' ? 900 : 1200)) + '" alt="' + esc(product.title || '') + '"' +
        ' class="' + mediaClass + '" loading="' + (index === 0 ? 'eager' : 'lazy') + '" data-lightbox-src="' + esc(media.src || '') + '">' +
      colorOverlayHTML + '</div>';
  }

  function nativeGalleryHTML(mediaList, product, colorOverlayHTML) {
    var mobileItems = mediaList.map(function (media, index) {
      return '<div class="product-media-gallery__carousel-item" data-media-id="' + esc(media.id || ('flash-media-' + index)) + '"' +
        ' data-media-type="' + esc(media.type) + '" data-media-index="' + index + '" data-index="' + index + '">' +
        nativeMediaMarkup(media, product, index, 'mobile', index === 0 ? colorOverlayHTML : '') + '</div>';
    }).join('');
    var thumbnails = mediaList.map(function (media, index) {
      var src = media.type === 'video' ? media.poster : media.src;
      return '<button type="button" class="product-media-gallery__thumbnail' + (index === 0 ? ' is-active' : '') + '"' +
        ' data-media-index="' + index + '" data-index="' + index + '" aria-label="View image ' + (index + 1) + '">' +
        (src ? '<img src="' + esc(resizeImage(src, 200)) + '" alt="" loading="lazy" class="product-media-gallery__thumbnail-image">' : '') +
        '</button>';
    }).join('');
    var first = mediaList[0];
    if (!first) return '';
    return '<div class="product-media-gallery product-media-gallery--thumbnail_left product-media-gallery--square"' +
      ' style="--gallery-aspect-ratio:1 / 1;--gallery-aspect-ratio-mobile:1 / 1;--gallery-border-radius:0px;--gallery-thumbnail-border-radius:4px;--gallery-object-fit:cover;--gallery-object-fit-mobile:cover;--gallery-grid-gap:4px;--gallery-mobile-thumbnail-size:72px;--gallery-desktop-thumbnail-size:96px;--gallery-thumbnail-gap:4px;--gallery-thumbnail-row-gap:4px;--gallery-thumbnail-padding-top:0px;--gallery-thumbnail-padding-bottom:4px;--gallery-thumbnail-padding-left:4px;--gallery-thumbnail-padding-right:4px;--gallery-thumbnail-aspect-ratio:1 / 1;--gallery-thumbnail-object-fit:cover;--block-padding-top:0px;--block-padding-bottom:8px;--block-padding-left:0px;--block-padding-right:0px;"' +
      ' data-layout="thumbnail_left" data-mobile-layout="thumbnail" data-thumbnail-active-border="true">' +
        '<div class="product-media-gallery__mobile-thumbnail-layout">' +
          '<div class="product-media-gallery__mobile-main"><div class="product-media-gallery__carousel-slider" data-pdp-media-slider>' + mobileItems + '</div></div>' +
          (mediaList.length > 1 ? '<div class="product-media-gallery__mobile-thumbnails"><div class="product-media-gallery__thumbnails-scroll">' + thumbnails + '</div></div>' : '') +
        '</div>' +
        '<div class="product-media-gallery__desktop"><div class="product-media-gallery__thumbnail-layout">' +
          '<div class="product-media-gallery__featured" data-pdp-featured-media data-media-index="0" data-media-id="' + esc(first.id || 'flash-media-0') + '" data-media-type="' + esc(first.type) + '">' +
            nativeMediaMarkup(first, product, 0, 'desktop', colorOverlayHTML) + '</div>' +
          (mediaList.length > 1 ? '<div class="product-media-gallery__desktop-thumbnails"><div class="product-media-gallery__thumbnails-scroll">' + thumbnails + '</div></div>' : '') +
        '</div></div>' +
      '</div>';
  }

  function renderPDP(pdpArea, product, deal, saleMap) {
    var variants = Array.isArray(product.variants) ? product.variants : [];
    var firstVariant = findFirstAvailableVariant(variants) || variants[0] || null;
    var form = miniProductForm();
    var options = form ? form.normalizeOptions(product.options || [], variants) : (product.options || []);
    var mediaList = buildMediaList(product);

    var colorName = String((deal && deal.colorName) || (deal && deal.color) || '').replace(/_/g, ' ').trim();
    var colorHex = deal && deal.hex;
    var colorSlug = deal && deal.color;
    var colorTagHTML = flashColorTagHTML(colorName, colorHex, colorSlug);
    var colorOverlayHTML = flashColorOverlayHTML(colorName, colorHex, colorSlug) +
      (isLimited(deal && deal.edition) ? '<span class="flash-deals__pdp-limited">Limited Edition</span>' : '');
    var galleryHTML = nativeGalleryHTML(mediaList, product, colorOverlayHTML);

    var initialPrices = dealPriceBreakdownCents(deal, product, firstVariant);
    var price = moneyFromCents(initialPrices.deal);
    var comparePriceHTML = '';
    if (initialPrices.regular > initialPrices.deal) {
      comparePriceHTML = '<span class="product-price-block__flash-regular dept__pdp-compare">' + moneyFromCents(initialPrices.regular) + '</span>';
    }
    var listPriceHTML = initialPrices.list
      ? '<span class="product-price-block__flash-list flash-deals__pdp-list" data-pdp-list>List ' + moneyFromCents(initialPrices.list) + '</span>'
      : '<span class="product-price-block__flash-list flash-deals__pdp-list" data-pdp-list hidden></span>';
    var flashZapHTML = initialPrices.automatic
      ? miniFlashZapHTML()
      : miniFlashZapHTML().replace(' data-pdp-flash-zap ', ' data-pdp-flash-zap hidden ');

    // This is the same selection contract as the PDP: Size and Length are
    // deliberate choices, while non-fit options use the first sellable variant.
    var namedSelection = form
      ? form.initialSelection({
        options: options,
        variants: variants,
        currentVariant: firstVariant,
        deferredOptions: ['Size', 'Length']
      })
      : {};
    var selectedByIndex = {};
    options.forEach(function (option, index) {
      if (namedSelection[option.name] != null) selectedByIndex[index] = namedSelection[option.name];
    });
    var optionsHTML = '';
    options.forEach(function (option, optionIndex) {
      var name = (option && option.name) || 'Option';
      var values = (option && option.values) || [];
      if (name.toLowerCase() === 'title' && values.length === 1 && values[0] === 'Default Title') return;
      var isLength = String(name).toLowerCase() === 'length';
      var isSize = String(name).toLowerCase() === 'size';
      var selectedValue = selectedByIndex[optionIndex];

      optionsHTML +=
        '<div class="product-variants-block__option" data-pdp-option-group data-option-index="' + optionIndex + '"' +
          (isDeferredPdpOption(name) ? ' data-option-deferred' : '') + '>' +
          '<label class="product-variants-block__label" style="display:flex;align-items:center;justify-content:space-between;gap:8px;width:100%;">' +
            '<span class="product-variants-block__label-text"><span data-pdp-option-label-text>' + esc(name) + '</span>' +
              '<span class="product-variants-block__selected-value" data-pdp-selected-value>' +
                (selectedValue == null ? '' : ' ' + esc(selectedValue)) + '</span></span>' +
            (isSize ? '<button type="button" class="product-size-chart-link" data-pdp-kiwi-size-chart aria-label="Open Kiwi Size Chart for ' + esc(product.title) + '"><span class="ph-bold ph-ruler" aria-hidden="true"></span>Size Chart</button>' : '') +
          '</label>' +
          '<div class="product-variants-block__pills' + (isLength ? ' product-variants-block__pills--length' : '') + '" data-option-index="' + optionIndex + '" data-option-name="' + esc(name) + '">';
      values.forEach(function (value) {
        var active = selectedValue === value;
        optionsHTML +=
          '<button type="button" class="product-variants-block__pill' +
            (isLength ? ' product-variants-block__pill--length' : '') +
            (active ? ' active' : '') + '" data-pdp-option-button' +
            ' data-option-index="' + optionIndex + '" data-option-value="' + esc(value) + '">' +
            (isLength
              ? '<span class="product-variants-block__pill-main">' + esc(value) + '</span><small class="product-variants-block__pill-subtext">' + lengthHelper(value, product) + '</small>'
              : esc(value)) +
            '</button>';
      });
      optionsHTML += '</div></div>';
    });

    var code = getDiscountCode(deal, saleMap);
    var discountLabel = String((deal && deal.discountLabel) || '').trim().replace(/^flash deals?$/i, 'Clearance');
    var initialPendingLabel = miniPendingLabel(options, selectedByIndex);

    var descriptionHTML = '';
    if (product.description) {
      descriptionHTML =
        '<details class="dept__pdp-accordion">' +
          '<summary class="dept__pdp-accordion__summary">' +
            '<span class="dept__pdp-accordion__title">Product Details</span>' +
            '<span class="dept__pdp-accordion__icon"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg></span>' +
          '</summary>' +
          '<div class="dept__pdp-accordion__content-wrapper"><div class="dept__pdp-accordion__content">' +
            '<div class="dept__pdp-accordion__content-inner">' + product.description + '</div>' +
          '</div></div>' +
        '</details>';
    }

    pdpArea.innerHTML =
      '<div class="product-page flash-deals__native-product-page" data-pdp-mount>' +
        '<div class="product-page__inner dept__pdp-content-layer dept__pdp-inner--pending" data-pdp-content tabindex="-1">' +
          '<div class="product-media">' + galleryHTML + '</div>' +
          '<div class="product-info">' +
            '<div class="product-title-block" style="--block-padding-top:0px;--block-padding-bottom:8px;--block-padding-left:12px;--block-padding-right:12px;">' +
              '<h2 class="product-title-block__title flash-deals__native-title"><a href="' + esc(productUrl(product.handle)) + '">' + esc(product.title) + '</a></h2>' +
            '</div>' +
            '<div class="product-price-block product-price-block--inline product-price-block--flash-mini" style="--price-font-size:var(--font-size-2xl);--price-font-weight:500;--block-letter-spacing:-0.025em;--block-line-height:1.25;--block-padding-top:0px;--block-padding-bottom:12px;--block-padding-left:12px;--block-padding-right:12px;">' +
              flashZapHTML +
              '<span class="product-price-block__current product-price-block__current--on-sale" data-pdp-price>' + price + '</span>' +
              '<span data-pdp-compare>' + comparePriceHTML + '</span>' +
              listPriceHTML +
              (discountLabel ? '<span class="flash-deals__pdp-discount">' + esc(discountLabel) + '</span>' : '') +
            '</div>' +
            (code ? '<div class="flash-deals__pdp-code">Use code <strong>' + esc(code) + '</strong> at checkout</div>' : '') +
            '<div class="product-variants-block flash-deals__pdp-variants" style="--block-padding-top:8px;--block-padding-bottom:16px;--block-padding-left:12px;--block-padding-right:12px;--pill-height:var(--btn-height-md);--pill-padding-y:0px;--pill-padding-x:16px;--pill-font-size:var(--btn-font-size-md);--pill-border-radius:8px;--pill-min-width:48px;--pill-gap:8px;--pill-font-weight:500;--pill-text-transform:none;--pill-letter-spacing:0px;--pill-border-width:1px;">' +
              (colorTagHTML ? '<div class="product-variants-block__option product-variants-block__colorways flash-deals__native-color"><label class="product-variants-block__label">Color:<span class="product-variants-block__selected-value">' + colorTagHTML + '</span></label></div>' : '') +
              optionsHTML + '</div>' +
            '<div class="product-add-to-cart-block flash-deals__pdp-add-to-cart" style="--atc-btn-height:var(--btn-height-lg);--atc-btn-radius:var(--radius-lg);--qty-radius:var(--radius-lg);--qty-pad:0px;--qty-height:var(--btn-height-lg);--atc-element-gap:8px;--block-padding-top:8px;--block-padding-bottom:8px;--block-padding-left:12px;--block-padding-right:12px;">' +
              '<div class="product-add-to-cart-block__form"><div class="product-add-to-cart-block__row">' +
                '<div class="product-add-to-cart-block__quantity">' +
                  '<button type="button" class="product-add-to-cart-block__quantity-btn" data-pdp-quantity-minus aria-label="Decrease quantity" disabled><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M5 12h14"></path></svg></button>' +
                  '<input type="number" value="1" min="1" class="product-add-to-cart-block__quantity-input" data-pdp-quantity-input aria-label="Quantity">' +
                  '<button type="button" class="product-add-to-cart-block__quantity-btn" data-pdp-quantity-plus aria-label="Increase quantity"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M5 12h14"></path><path d="M12 5v14"></path></svg></button>' +
                '</div>' +
                '<div class="product-add-to-cart-block__btn-wrapper">' +
                  '<button type="button" class="btn btn--primary btn--standard btn--lg btn--squircle product-add-to-cart-block__btn' + (initialPendingLabel ? ' is-pending' : '') + '" data-pdp-atc data-variant-id="' + ((firstVariant && firstVariant.id) || '') + '">' +
                    '<span class="btn__text"><span data-add-to-cart-text>' + esc(initialPendingLabel || (isVariantAvailable(firstVariant) ? 'Add to Cart' : 'Sold Out')) + '</span></span>' +
                  '</button>' +
                '</div>' +
              '</div></div>' +
            '</div>' +
            descriptionHTML +
          '</div>' +
        '</div>' +
      '</div>';

    pdpArea.dataset.currentProductHandle = product.handle || '';
    pdpArea._pdpCurrentVariant = firstVariant;
    bindPDP(pdpArea, mediaList, variants, options, selectedByIndex, deal, product);

    // Native PDPs have Kiwi's app block on the page and begin loading the
    // chart before the shopper clicks. Warm the same chart in the background
    // so the mini-PDP click only opens the already-prepared Kiwi modal.
    var kiwi = window.HealixVariantSheet;
    var hasSize = Array.isArray(product.options) && product.options.some(function (option) {
      return String(option && option.name || '').trim().toLowerCase() === 'size';
    });
    if (kiwi && hasSize && typeof kiwi.preloadKiwiSizeChart === 'function') {
      kiwi.preloadKiwiSizeChart(product.handle, product).catch(function () {});
    }

    var content = pdpArea.querySelector('[data-pdp-content]');
    if (content) {
      requestAnimationFrame(function () {
        content.classList.remove('dept__pdp-inner--pending');
        content.classList.add('is-ready');
        content.focus({ preventScroll: true });
      });
    }
  }

  // PDP interactions. Variant state comes from HealixProductForm, the same
  // source used by the real PDP; this surface only owns its inline shell.
  function bindPDP(pdpArea, mediaList, variants, options, selectedByIndex, deal, product) {
    // Native PDP thumbnail/carousel contract.
    var thumbs = Array.prototype.slice.call(pdpArea.querySelectorAll('.product-media-gallery__thumbnail'));
    var slider = pdpArea.querySelector('[data-pdp-media-slider]');
    var featured = pdpArea.querySelector('[data-pdp-featured-media]');
    var colorName = String((deal && deal.colorName) || (deal && deal.color) || '').replace(/_/g, ' ').trim();
    var colorOverlayHTML = flashColorOverlayHTML(colorName, deal && deal.hex, deal && deal.color) +
      (isLimited(deal && deal.edition) ? '<span class="flash-deals__pdp-limited">Limited Edition</span>' : '');

    function activateMedia(index, scrollMobile) {
      if (index < 0 || index >= mediaList.length) return;
      thumbs.forEach(function (thumb) {
        thumb.classList.toggle('is-active', parseInt(thumb.dataset.mediaIndex, 10) === index);
      });
      if (featured) {
        featured.dataset.mediaIndex = String(index);
        featured.dataset.mediaId = String(mediaList[index].id || ('flash-media-' + index));
        featured.dataset.mediaType = mediaList[index].type;
        featured.innerHTML = nativeMediaMarkup(mediaList[index], product, index, 'desktop', colorOverlayHTML);
        if (mediaList[index].type === 'video') {
          requestAnimationFrame(function () {
            var video = featured.querySelector('video');
            if (video) {
              video.muted = true;
              video.playsInline = true;
              var playPromise = video.play();
              if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(function () {});
            }
          });
        }
      }
      if (scrollMobile && slider) {
        slider.scrollTo({ left: index * slider.clientWidth, behavior: 'smooth' });
      }
    }

    pdpArea._activateMiniMedia = activateMedia;
    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var idx = parseInt(thumb.dataset.mediaIndex, 10);
        activateMedia(idx, true);
      });
    });
    if (slider) {
      var scrollFrame = 0;
      slider.addEventListener('scroll', function () {
        if (scrollFrame) cancelAnimationFrame(scrollFrame);
        scrollFrame = requestAnimationFrame(function () {
          var index = slider.clientWidth ? Math.round(slider.scrollLeft / slider.clientWidth) : 0;
          thumbs.forEach(function (thumb) {
            thumb.classList.toggle('is-active', parseInt(thumb.dataset.mediaIndex, 10) === index);
          });
        });
      }, { passive: true });
    }

    var optionButtons = Array.prototype.slice.call(pdpArea.querySelectorAll('[data-pdp-option-button]'));
    optionButtons.forEach(function (button) {
      button.addEventListener('click', function () {
        if (button.disabled) return;
        var optionIndex = parseInt(button.dataset.optionIndex, 10);
        selectedByIndex[optionIndex] = button.dataset.optionValue || '';
        var group = button.closest('[data-pdp-option-group]');
        if (group) clearMiniPdpPrompt(group);
        updateOptionAvailability(pdpArea, variants, options, selectedByIndex);
        syncVariantState(pdpArea, mediaList, variants, options, selectedByIndex, deal);
      });
    });

    var sizeChart = pdpArea.querySelector('[data-pdp-kiwi-size-chart]');
    if (sizeChart) {
      sizeChart.addEventListener('click', function () {
        var sheet = window.HealixVariantSheet;
        if (!sheet || typeof sheet.openKiwiSizeChart !== 'function') return;
        sheet.openKiwiSizeChart(product.handle, sizeChart, product);
      });
    }

    updateOptionAvailability(pdpArea, variants, options, selectedByIndex);
    syncVariantState(pdpArea, mediaList, variants, options, selectedByIndex, deal);

    // Add to cart (reuses the global CartAPI like the Department Section).
    var atcButton = pdpArea.querySelector('[data-pdp-atc]');
    var quantityInput = pdpArea.querySelector('[data-pdp-quantity-input]');
    var quantityMinus = pdpArea.querySelector('[data-pdp-quantity-minus]');
    var quantityPlus = pdpArea.querySelector('[data-pdp-quantity-plus]');
    function quantityValue() {
      return Math.max(1, parseInt(quantityInput && quantityInput.value, 10) || 1);
    }
    function syncQuantity() {
      if (!quantityInput) return;
      quantityInput.value = String(quantityValue());
      if (quantityMinus) quantityMinus.disabled = quantityValue() <= 1;
    }
    if (quantityMinus) quantityMinus.addEventListener('click', function () {
      quantityInput.value = String(Math.max(1, quantityValue() - 1));
      syncQuantity();
    });
    if (quantityPlus) quantityPlus.addEventListener('click', function () {
      quantityInput.value = String(quantityValue() + 1);
      syncQuantity();
    });
    if (quantityInput) quantityInput.addEventListener('change', syncQuantity);
    syncQuantity();
    if (atcButton) {
      atcButton.addEventListener('click', function () {
        if (miniMissingOptions(options, selectedByIndex).length) {
          promptMiniPdpSelection(pdpArea, options, selectedByIndex);
          return;
        }
        var variantId = parseInt(atcButton.dataset.variantId, 10);
        if (!variantId) return;
        var originalText = (atcButton.querySelector('[data-add-to-cart-text]') || atcButton).textContent || 'Add to Cart';
        atcButton.classList.add('is-loading', 'btn--loading');
        atcButton.disabled = true;
        setMiniPdpAtcText(atcButton, 'Adding...');
        var add = addMiniPdpCartItem(variantId, quantityValue());
        add.then(function () {
          atcButton.classList.remove('is-loading', 'btn--loading');
          setMiniPdpAtcText(atcButton, 'Added!');
          setTimeout(function () { syncVariantState(pdpArea, mediaList, variants, options, selectedByIndex, deal); }, 2000);
        }).catch(function () {
          atcButton.classList.remove('is-loading', 'btn--loading');
          setMiniPdpAtcText(atcButton, 'Error - Try Again');
          setTimeout(function () {
            syncVariantState(pdpArea, mediaList, variants, options, selectedByIndex, deal);
          }, 2000);
        });
      });
    }
  }

  function addMiniPdpCartItem(variantId, quantity) {
    quantity = Math.max(1, parseInt(quantity, 10) || 1);
    if (window.CartAPI && typeof window.CartAPI.addItem === 'function') {
      return window.CartAPI.addItem(variantId, quantity);
    }
    var formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', quantity);
    return fetch('/cart/add.js', { method: 'POST', body: formData }).then(function (response) {
      if (!response.ok) {
        return response.json().then(function (error) {
          throw new Error(error.description || 'Failed to add to cart');
        });
      }
      return response.json();
    }).then(function (addedItem) {
      return fetch('/cart.js').then(function (response) { return response.json(); }).then(function (cart) {
        window.dispatchEvent(new CustomEvent('cart:item-added', { detail: { item: addedItem, cart: cart } }));
        window.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
        return addedItem;
      });
    });
  }

  function setMiniPdpAtcText(button, value) {
    var text = button && button.querySelector('[data-add-to-cart-text]');
    if (text) text.textContent = value;
    else if (button) button.textContent = value;
  }

  function clearMiniPdpPrompt(group) {
    if (!group) return;
    group.classList.remove('is-prompt');
    var label = group.querySelector('.product-variants-block__prompt-msg');
    if (label) {
      label.textContent = label.getAttribute('data-original-label') || '';
      label.removeAttribute('data-original-label');
      label.classList.remove('product-variants-block__prompt-msg');
    }
  }

  function promptMiniPdpSelection(pdpArea, options, selectedByIndex) {
    var missing = miniMissingOptions(options, selectedByIndex);
    if (!missing.length) return;
    var index = options.indexOf(missing[0]);
    var group = pdpArea.querySelector('[data-pdp-option-group][data-option-index="' + index + '"]');
    if (!group) return;
    var label = group.querySelector('[data-pdp-option-label-text]');
    if (!label) return;
    if (!label.classList.contains('product-variants-block__prompt-msg')) {
      label.setAttribute('data-original-label', label.textContent.trim());
      label.classList.add('product-variants-block__prompt-msg');
    }
    label.textContent = 'Please select a ' + String(missing[0].name).toLowerCase();
    group.classList.remove('is-prompt');
    void group.offsetWidth;
    group.classList.add('is-prompt');
    group.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function syncMiniPdpMedia(pdpArea, mediaList, variant) {
    if (!variant || !mediaList.length) return;
    var featured = variant.featured_media || variant.featured_image || {};
    var featuredId = String(featured.id || '');
    var featuredSrc = String(featured.src || featured.url || '');
    var index = mediaList.findIndex(function (media) {
      return (featuredId && String(media.id || '') === featuredId) ||
        (featuredSrc && String(media.src || media.poster || '') === featuredSrc);
    });
    if (index < 0) return;
    if (typeof pdpArea._activateMiniMedia === 'function') pdpArea._activateMiniMedia(index, true);
  }

  function syncVariantState(pdpArea, mediaList, variants, options, selectedByIndex, deal) {
    if (!variants.length) return;
    var form = miniProductForm();
    var namedSelection = miniSelectionByName(options, selectedByIndex);
    var matched = form ? form.findVariant(variants, options, namedSelection) : null;
    var displayVariant = matched || pdpArea._pdpCurrentVariant || findFirstAvailableVariant(variants);
    if (matched) pdpArea._pdpCurrentVariant = matched;
    var priceNode = pdpArea.querySelector('[data-pdp-price]');
    var compareNode = pdpArea.querySelector('[data-pdp-compare]');
    var listNode = pdpArea.querySelector('[data-pdp-list]');
    var zapNode = pdpArea.querySelector('[data-pdp-flash-zap]');
    var atcButton = pdpArea.querySelector('[data-pdp-atc]');

    var prices = dealPriceBreakdownCents(deal, {}, displayVariant);
    if (priceNode) priceNode.textContent = moneyFromCents(prices.deal);
    if (compareNode) {
      if (prices.regular > prices.deal) {
        compareNode.innerHTML = '<span class="product-price-block__flash-regular dept__pdp-compare">' + moneyFromCents(prices.regular) + '</span>';
      } else {
        compareNode.innerHTML = '';
      }
    }
    if (listNode) {
      listNode.textContent = prices.list ? 'List ' + moneyFromCents(prices.list) : '';
      listNode.hidden = !prices.list;
    }
    if (zapNode) zapNode.hidden = !prices.automatic;
    if (matched) syncMiniPdpMedia(pdpArea, mediaList, matched);
    if (atcButton) {
      var pendingLabel = miniPendingLabel(options, selectedByIndex);
      if (pendingLabel) {
        atcButton.classList.add('is-pending');
        atcButton.dataset.variantId = '';
        atcButton.disabled = false;
        setMiniPdpAtcText(atcButton, pendingLabel);
        return;
      }
      var sellable = isVariantAvailable(matched);
      atcButton.classList.remove('is-pending');
      atcButton.dataset.variantId = matched && matched.id ? matched.id : '';
      atcButton.disabled = !sellable;
      setMiniPdpAtcText(atcButton, sellable ? 'Add to Cart' : 'Sold Out');
    }
  }

  function updateOptionAvailability(pdpArea, variants, options, selectedByIndex) {
    if (!variants || !variants.length) return;
    var form = miniProductForm();
    var namedSelection = miniSelectionByName(options, selectedByIndex);
    var rows = Array.prototype.slice.call(pdpArea.querySelectorAll('.product-variants-block__pills[data-option-index]'));
    rows.forEach(function (row) {
      var optionIndex = parseInt(row.dataset.optionIndex, 10);
      if (isNaN(optionIndex)) return;
      var selectedValue = selectedByIndex[optionIndex];
      var selectedLabel = row.closest('[data-pdp-option-group]')?.querySelector('[data-pdp-selected-value]');
      if (selectedLabel) selectedLabel.textContent = selectedValue == null ? '' : ' ' + selectedValue;
      Array.prototype.slice.call(row.querySelectorAll('[data-pdp-option-button]')).forEach(function (button) {
        var value = button.dataset.optionValue || '';
        var selectable = form
          ? form.isOptionValueAvailable(variants, options, namedSelection, optionIndex, value)
          : false;
        var disable = !selectable;
        button.disabled = disable;
        button.classList.toggle('is-unavailable', disable);
        button.classList.toggle('active', selectedValue === value);
      });
    });
  }

  // ── Smooth scroll (mirror dept) ────────────────────────────────────────────
  function getStickyHeaderOffset() {
    try {
      var v = getComputedStyle(document.documentElement).getPropertyValue('--untamed-fixed-header-offset');
      var n = parseInt(v, 10);
      return isNaN(n) ? 0 : n;
    } catch (e) { return 0; }
  }
  function smoothScrollToElement(el) {
    if (!el) return;
    var top = el.getBoundingClientRect().top + window.pageYOffset - getStickyHeaderOffset() - 16;
    try { window.scrollTo({ top: top, behavior: 'smooth' }); }
    catch (e) { window.scrollTo(0, top); }
  }

  // ── Per-section init ──────────────────────────────────────────────────────
  function initSection(root) {
    if (!root || root.dataset.flashDealsInitialized === 'true') return;
    root.dataset.flashDealsInitialized = 'true';

    var bodyEl = root.querySelector('[data-flash-deals-body]');
    var pdpArea = root.querySelector('[data-flash-deals-pdp]');
    var filtersEl = root.querySelector('[data-flash-deals-filters]');
    if (!bodyEl || !pdpArea) return;

    var showEmpty = root.dataset.showEmpty !== 'false';
    var emptyText = root.dataset.emptyText || 'No Clearance items are live right now.';
    var saleMap = {};
    var dealIndex = {}; // handle -> deal meta (scoped to this section)

    initCountdown(root);

    function openPDP(handle, cardEl) {
      var deal = dealIndex[handle];
      root.querySelectorAll('.flash-deals__card.is-active').forEach(function (el) { el.classList.remove('is-active'); });
      if (cardEl) cardEl.classList.add('is-active');

      pdpArea.innerHTML = loadingPDPHTML();
      smoothScrollToElement(pdpArea);

      window.HealixColors.product(handle).then(function (product) {
        if (pdpArea.dataset.pendingHandle && pdpArea.dataset.pendingHandle !== handle) return;
        if (!product) {
          pdpArea.innerHTML = '<p style="color:#a1a1aa;padding:16px;">Could not load product details. ' +
            '<a href="' + esc(productUrl(handle)) + '">View product</a></p>';
          return;
        }
        renderPDP(pdpArea, product, deal, saleMap);
      }).catch(function () {
        pdpArea.innerHTML = '<p style="color:#a1a1aa;padding:16px;">Could not load product details. ' +
          '<a href="' + esc(productUrl(handle)) + '">View product</a></p>';
      });
      pdpArea.dataset.pendingHandle = handle;
    }

    function cardFromEvent(e) {
      var el = e.target && e.target.closest ? e.target.closest('[data-flash-deals-card]') : null;
      return el && root.contains(el) ? el : null;
    }

    // Cards contain native <button>s (image + claim) which fire click on mouse
    // and keyboard (Enter/Space) and bubble here — one handler covers both.
    bodyEl.addEventListener('click', function (e) {
      var cardEl = cardFromEvent(e);
      if (!cardEl) return;
      e.preventDefault();
      openPDP(cardEl.getAttribute('data-handle'), cardEl);
    });

    // Stateful gender + category filtering. Cards carry data-deals-gender and
    // data-deals-category; both must match the active selection to stay visible.
    var gendersEl = root.querySelector('[data-flash-deals-genders]');
    var currentGender = '';
    var currentCategory = '';
    var allDealsRef = [];

    function applyFilters() {
      bodyEl.querySelectorAll('[data-flash-deals-card]').forEach(function (card) {
        var g = card.getAttribute('data-deals-gender') || '';
        var c = card.getAttribute('data-deals-category') || '';
        var genderOk = !currentGender || !g || g === currentGender; // shared (no gender) shows in both
        var catOk = !currentCategory || c === currentCategory;
        card.hidden = !(genderOk && catOk);
      });
    }

    // Rebuild category pills for the active gender (deals with no gender are shared),
    // resetting to "All". Keeps category tabs scoped to the chosen gender.
    function refreshCategories() {
      if (!filtersEl) return;
      var seen = {};
      var types = [];
      allDealsRef.forEach(function (d) {
        if (!d._category) return;
        if (currentGender && d._gender && d._gender !== currentGender) return;
        var k = handleizeJS(d._category);
        if (!seen[k]) { seen[k] = true; types.push(d._category); }
      });
      currentCategory = '';
      if (types.length) buildPills(filtersEl, types);
      else filtersEl.innerHTML = '';
    }

    if (filtersEl) {
      filtersEl.addEventListener('click', function (e) {
        var btn = e.target && e.target.closest ? e.target.closest('[data-fd-pill]') : null;
        if (!btn || !filtersEl.contains(btn)) return;
        currentCategory = btn.getAttribute('data-fd-pill') || '';
        filtersEl.querySelectorAll('.flash-deals__pill').forEach(function (b) {
          var active = b === btn;
          b.classList.toggle('is-active', active);
          b.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        applyFilters();
      });
    }

    if (gendersEl) {
      gendersEl.addEventListener('click', function (e) {
        var btn = e.target && e.target.closest ? e.target.closest('[data-fd-gender]') : null;
        if (!btn || !gendersEl.contains(btn)) return;
        currentGender = btn.getAttribute('data-fd-gender') || '';
        gendersEl.querySelectorAll('.flash-deals__gender').forEach(function (b) {
          var active = b === btn;
          b.classList.toggle('is-active', active);
          b.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        refreshCategories(); // pills scoped to the chosen gender, reset to All
        applyFilters();
      });
    }

    if (!window.HealixColors || typeof window.HealixColors.deals !== 'function') {
      renderEmpty(bodyEl, emptyText, showEmpty);
      return;
    }

    renderSkeleton(bodyEl);

    window.HealixColors.deals().then(function (data) {
      var groups = (data && Array.isArray(data.byColor)) ? data.byColor : [];
      saleMap = buildSaleMap(data && data.sales);

      // Flatten all colour groups into a single de-duplicated deal list.
      var seen = {};
      var allDeals = [];
      groups.forEach(function (grp) {
        (Array.isArray(grp.products) ? grp.products : []).forEach(function (p) {
          if (!p || !p.handle || seen[p.handle]) return;
          seen[p.handle] = true;
          var deal = {
            handle: p.handle, title: p.title, discountLabel: p.discountLabel, saleId: p.saleId,
            color: p.color || grp.color, colorName: p.colorName || grp.colorName,
            hex: p.hex || grp.hex, edition: p.edition || grp.edition
          };
          deal._sale = saleMap[p.saleId] || null;
          deal._code = getDiscountCode(deal, saleMap);
          deal._family = familyLabel(deal);
          dealIndex[p.handle] = deal;
          allDeals.push(deal);
        });
      });

      if (!allDeals.length) { renderEmpty(bodyEl, emptyText, showEmpty); return; }

      // Category = Shopify product type (the coarse Tops/Bottoms/Jumpsuits/Accessories
      // grouping). It is NOT in the deals payload, so we read it from the enriched
      // product (/products/{handle}.js → product.type). HealixColors.product caches,
      // so this pre-fetch and enrichCard's fetch share one request per product.
      Promise.all(allDeals.map(function (deal) {
        return window.HealixColors.product(deal.handle).then(function (p) {
          deal._product = p || null;
          deal._category = (p && p.type) ? String(p.type).trim() : '';
          deal._gender = genderForDeal(deal);
          return deal;
        }).catch(function () {
          deal._category = '';
          deal._gender = genderFromHandle(deal.handle); // handle works even if enrichment fails
          return deal;
        });
      })).then(function () {
        allDealsRef = allDeals;

        // Gender presence — entirely data-driven from the products the API returned.
        var mensProducts = allDeals.filter(function (d) { return d._gender === 'men'; });
        var womensProducts = allDeals.filter(function (d) { return d._gender === 'women'; });
        var hasMensProducts = mensProducts.length > 0;
        var hasWomensProducts = womensProducts.length > 0;
        var bothGenders = hasMensProducts && hasWomensProducts;

        // Gender switcher ONLY when BOTH genders exist (same availability rule as
        // the collection template). Otherwise hide it and show categories directly.
        if (gendersEl) {
          if (bothGenders) {
            gendersEl.hidden = false;
            gendersEl.innerHTML =
              '<button type="button" class="flash-deals__gender is-active" data-fd-gender="men" aria-pressed="true">Men</button>' +
              '<button type="button" class="flash-deals__gender" data-fd-gender="women" aria-pressed="false">Women</button>';
            currentGender = 'men';
          } else {
            gendersEl.hidden = true;
            gendersEl.innerHTML = '';
            currentGender = (hasWomensProducts && !hasMensProducts) ? 'women'
              : (hasMensProducts ? 'men' : '');
          }
        }

        // Build the grid; cards carry data-deals-gender + data-deals-category.
        var grid = document.createElement('div');
        grid.className = 'flash-deals__grid';
        allDeals.forEach(function (deal) {
          var card = buildCard(deal);
          grid.appendChild(card);
          enrichCard(card, deal);
        });

        bodyEl.innerHTML = '';
        bodyEl.appendChild(grid);
        bodyEl.setAttribute('aria-busy', 'false');

        // Category pills scoped to the active gender, then apply the initial filter.
        refreshCategories();
        applyFilters();
      });
    }).catch(function () {
      renderEmpty(bodyEl, emptyText, showEmpty);
    });
  }

  function initAll() {
    document.querySelectorAll('[data-flash-deals]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Shopify theme editor lifecycle: (re)init when the section is added/reloaded.
  document.addEventListener('shopify:section:load', function (e) {
    var el = e.target && e.target.querySelector ? e.target.querySelector('[data-flash-deals]') : null;
    if (el) initSection(el);
  });
})();
